import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import Modal from 'react-modal';
import {
  GSPLUS_SIGNUP_EN_URL,
  GSPLUS_SIGNUP_FR_URL,
} from '../../utils/environment';
import gsLogo from '../../images/migration/gs_logo.svg';
import Close from '../../images/migration/close.svg';

const LEARN_MORE_URL =
  'https://inkblot.zendesk.com/hc/en-us/articles/25450855312660';

const Overlay = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(15, 32, 69, 0.6)',
};

const BodyContainer = {
  display: 'block',
  maxWidth: 648,
  width: '100%',
  boxShadow: '0 0 50px 0 rgba(40, 71, 140, 0.2)',
  top: 'auto',
  left: 'auto',
  right: 'auto',
  bottom: 'auto',
  borderRadius: 12,
};

const Top = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  paddingTop: 10,
  paddingBottom: 20,
};

const CloseButton = {
  position: 'absolute',
  cursor: 'pointer',
  backgroundColor: 'transparent',
  right: 0,
  top: 0,
};

const Center = { textAlign: 'center', margin: '0 auto', width: '95%' };

const Attention = {
  color: '#09709c',
  textAlign: 'center',
  fontFamily: 'Gilroy',
  fontSize: '15px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '20px',
};

const Title = {
  color: '#303333',
  textAlign: 'center',
  fontFamily: 'Gilroy',
  fontSize: '30px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: '140%',
};

const Text = {
  color: '#545959',
  textAlign: 'center',
  fontFamily: 'Gilroy',
  fontSize: '17px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.25px',
  marginTop: 8,
};

const Link = {
  color: '#09709c',
  fontFamily: 'Gilroy',
  fontSize: '15px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '20px',
  textDecorationLine: 'underline',
  cursor: 'pointer',
  marginTop: 20,
};

const ButtonContainer = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap-reverse',
  gap: '8px',
  marginTop: 44,
};

const StyledButton = (outline, full) => ({
  width: full ? '95%' : 296,
  height: 48,
  borderRadius: 999,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: `1px solid ${outline ? '#d0dbdb' : 'transparent'}`,
  backgroundColor: outline ? 'transparent' : '#00544E',
  color: outline ? '#545959' : '#D9F0FA',
  paddingLeft: '1.5rem',
  paddingRight: '1.5rem',
  cursor: 'pointer',
});

const StyledButtonLabel = {
  fontFamily: 'Gilroy',
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.02em',
  flex: 'none',
  order: 1,
  flexGrow: 0,
  width: '100%',
};

const Button = props => {
  // eslint-disable-next-line react/prop-types
  const { onClick, disable, label, input, outline, full } = props;

  return (
    <>
      <button
        style={StyledButton(outline, full)}
        type="button"
        onClick={onClick}
        disable={disable}
        {...input}
        {...props}
      >
        <span style={StyledButtonLabel}>{label}</span>
      </button>
    </>
  );
};

function MigrationPopup({ open, onClose, noClose, intl: { locale } }) {
  if (!open) return <></>;

  return (
    <>
      <Modal isOpen={open} style={{ overlay: Overlay, content: BodyContainer }}>
        <div style={Top}>
          <img src={gsLogo} alt="Green Shield Plus logo" />

          {!noClose && (
            <button
              className="close"
              style={CloseButton}
              onClick={onClose}
              aria-label="modal close button"
              aria-describedby="close-modal-button-desc-for-screen-reader"
              type="button"
            >
              <img src={Close} alt="close modal" />
            </button>
          )}
        </div>

        <div style={Center}>
          <div style={Attention}>
            <FormattedMessage
              id="migration.attention"
              defaultMessage="Attention"
            />
          </div>
          <div style={Title}>
            <FormattedMessage
              id="migration.news"
              defaultMessage="Exciting News: Upgrade to GreenShield+"
            />
          </div>
          <div style={Text}>
            <FormattedMessage
              id="migration.redirect"
              defaultMessage="You're being redirected because we have upgraded to GreenShield+, providing a sleeker, more user-friendly experience for all mental health services previously accessed on Inkblot. Sign up on the new site now to begin exploring GreenShield+! We're excited for you to get started!"
            />
          </div>
          <div
            role="link"
            style={Link}
            tabIndex="0"
            onClick={() => {
              window.location.assign(LEARN_MORE_URL);
            }}
            onKeyUp={event => {
              if (event.key === 'Enter') {
                window.location.assign(LEARN_MORE_URL);
              }
            }}
          >
            <FormattedMessage
              id="migration.learnMore"
              defaultMessage="Learn more about this change"
            />
          </div>
        </div>
        <div style={ButtonContainer}>
          {!noClose && (
            <Button
              outline
              label={
                <FormattedMessage
                  id="migration.comeBack"
                  defaultMessage="Come back later"
                />
              }
              onClick={onClose}
            />
          )}
          <Button
            label={
              <FormattedMessage
                id="migration.goTo"
                defaultMessage="Go to GreenShield+"
              />
            }
            onClick={() =>
              window.location.assign(
                locale === 'fr' ? GSPLUS_SIGNUP_FR_URL : GSPLUS_SIGNUP_EN_URL,
              )
            }
            full={noClose}
          />
        </div>
      </Modal>
    </>
  );
}

MigrationPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  noClose: PropTypes.bool,
  intl: intlShape.isRequired,
};

MigrationPopup.defaultProps = {};

export default injectIntl(MigrationPopup);
